import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="w-full min-h-screen bg-gray-50 text-gray-800 font-sans">
      {/* Container */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <h1 className="text-3xl font-bold mb-2 text-center">Privacy Policy</h1>
        <p className="text-center text-sm text-gray-500 mb-10">
          <span className="font-semibold">Effective Date:</span> [04-Sept-2025]
          | <span className="font-semibold">Last Updated:</span> [04-Sept-2025]
        </p>

        {/* Section */}
        <p className="mb-6">
          <span className="font-semibold">Empfly Pulse</span> (“we”, “our”,
          “us”) is committed to protecting your privacy. This Privacy Policy
          explains how we collect, use, disclose, and safeguard your personal
          information when you use our mobile app, website, and related services
          (collectively, <span className="italic">“Empfly Pulse Platform”</span>
          ).
        </p>
        <p className="mb-10">
          We prioritize your trust and are fully compliant with the{" "}
          <span className="italic">
            Digital Personal Data Protection Act, 2023
          </span>
          , and other applicable Indian and international privacy regulations.
        </p>

        {/* Sections */}
        <div className="space-y-10">
          {/* 1 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">1. What We Collect</h2>
            <p className="mb-2">
              We may collect the following types of personal data:
            </p>
            <h3 className="font-medium">a) Personal Identifiers</h3>
            <ul className="list-disc ml-6 mb-3">
              <li>Name, phone number, email address</li>
              <li>Age, gender, location (optional)</li>
              <li>Consent preferences</li>
            </ul>

            <h3 className="font-medium">b) Health & Wellness Data</h3>
            <ul className="list-disc ml-6 mb-3">
              <li>Physical, emotional, and nutritional inputs</li>
              <li>Wellness scores and symptom tracking</li>
              <li>Menstrual and reproductive history (if provided)</li>
              <li>
                Cancer diagnosis status and treatment stage (if applicable)
              </li>
            </ul>

            <h3 className="font-medium">c) Device & Usage Data</h3>
            <ul className="list-disc ml-6 mb-3">
              <li>IP address, device ID, operating system</li>
              <li>App usage statistics and session logs</li>
              <li>Cookies (on the website) and analytics tools</li>
            </ul>

            <h3 className="font-medium">d) AI Interaction Data</h3>
            <ul className="list-disc ml-6">
              <li>
                Conversations and inputs shared with Tara (our AI chatbot)
              </li>
              <li>Responses to self-assessment tools and wellness prompts</li>
            </ul>
          </div>

          {/* 2 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">
              2. Why We Collect It (Purpose of Use)
            </h2>
            <ul className="list-disc ml-6">
              <li>Personalize your wellness journey and recommend modules</li>
              <li>Assign wellness scores and insights</li>
              <li>Connect you to expert-led support</li>
              <li>Improve Tara’s AI response accuracy and empathy</li>
              <li>Enable early detection tools and guided self-checks</li>
              <li>Assist with appointment bookings and escalations</li>
              <li>Comply with medical, legal, and ethical standards</li>
            </ul>
          </div>

          {/* 3 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">
              3. How We Use Your Data
            </h2>
            <ul className="list-disc ml-6">
              <li>
                Do <span className="font-semibold">not</span> sell or rent your
                data
              </li>
              <li>Anonymize data for research and insights</li>
              <li>
                Use encrypted AI learning to improve system performance (opt-in
                only)
              </li>
              <li>
                Share with certified medical professionals only upon your
                consent
              </li>
            </ul>
          </div>

          {/* 4 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">
              4. Who We Share Data With
            </h2>
            <p className="mb-2">
              Data may be shared{" "}
              <span className="italic">only with your explicit consent</span>{" "}
              and for purposes such as:
            </p>
            <ul className="list-disc ml-6">
              <li>Connecting you with wellness experts and therapists</li>
              <li>Generating reports for caregivers or healthcare providers</li>
              <li>Booking diagnostics (only if you opt-in)</li>
              <li>Legal or regulatory authorities (if required by law)</li>
            </ul>
          </div>

          {/* 5 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">
              5. AI Model & Human Oversight
            </h2>
            <ul className="list-disc ml-6">
              <li>
                Our AI assistant “Tara” is designed with empathy and caution.
              </li>
              <li>AI interactions are monitored for accuracy and safety.</li>
              <li>
                Red-flag behaviors or crisis signals are escalated to a human
                expert.
              </li>
              <li>
                AI learning is disabled by default and enabled only through
                consent.
              </li>
            </ul>
          </div>

          {/* 6 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">6. Data Security</h2>
            <ul className="list-disc ml-6">
              <li>Data is encrypted at rest and in transit</li>
              <li>Multi-layer authentication for medical personnel</li>
              <li>Periodic audits and vulnerability assessments</li>
              <li>Storage on GDPR/HIPAA-compliant infrastructure</li>
            </ul>
          </div>

          {/* 7 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">7. Your Rights</h2>
            <p className="mb-2">
              You have full control over your data. You may:
            </p>
            <ul className="list-disc ml-6">
              <li>Request access or correction</li>
              <li>Withdraw consent or delete your account</li>
              <li>Export or port your personal data</li>
              <li>Opt-out of AI learning at any time</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, email us at{" "}
              <a
                href="mailto:support@empfly.com"
                className="text-blue-600 underline"
              >
                support@empfly.com
              </a>
            </p>
          </div>

          {/* 8 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">
              8. Consent, Legal Grounds, and Age Requirements
            </h2>
            <ul className="list-disc ml-6">
              <li>
                Use of Empfly Pulse requires{" "}
                <span className="italic">informed consent</span> (recorded
                during onboarding)
              </li>
              <li>Users must be 18+ years or have consent from a guardian</li>
              <li>
                Medical content is for informational purposes only and not a
                substitute for licensed clinical care
              </li>
            </ul>
          </div>

          {/* 9 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">
              9. Third-Party Services
            </h2>
            <p>
              We may use trusted third-party tools (e.g., secure telehealth
              platforms, payment gateways, analytics). All such services are
              vetted for DPDP/IT Act/GDPR compliance.
            </p>
          </div>

          {/* 10 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">10. Data Retention</h2>
            <ul className="list-disc ml-6">
              <li>
                Personal data is retained only as long as necessary for its
                intended use
              </li>
              <li>
                Upon account deletion, we remove identifiable data within 15
                days
              </li>
            </ul>
          </div>

          {/* 11 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">
              11. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy to reflect changes in legal,
              technical, or business developments. We’ll notify you via email or
              in-app alerts before significant changes take effect.
            </p>
          </div>

          {/* 12 */}
          <div>
            <h2 className="text-xl font-semibold mb-3">12. Contact Us</h2>
            <p>
              For any questions, concerns, or data requests:
              <br />
              📧{" "}
              <a
                href="mailto:support@empfly.com"
                className="text-blue-600 underline"
              >
                support@empfly.com
              </a>
              <br />
              <a href="tel:9311440991">📞 9311440991</a>
              <br />
              🏢 Empfly Services Pvt. Ltd., Gurgaon, India
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
